import { useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useState, type FormEvent } from "react";

import { requestPasswordReset } from "@/auth/backend";
import { useSignInSearch } from "@/auth/sign-in-search";
import { useSession } from "@/auth/session";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * "Send us a link" (M10-6), inline on the sign-in screen rather than its own
 * route: there is nothing here a URL needs to name, and the confirmation is
 * the same sentence regardless of what was typed — see the doc comment on
 * `requestPasswordReset` for why it never says whether the address exists.
 */
function ForgotPasswordForm({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  if (sent) {
    return (
      <div className="bg-card w-full max-w-sm space-y-4 rounded-xl border p-6 text-center shadow-sm">
        <p className="text-sm">
          If <strong>{email}</strong> has an account, we sent a link to reset
          its password.
        </p>
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={onBack}
        >
          Back to sign in
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        void requestPasswordReset(email).then(() => {
          setBusy(false);
          setSent(true);
        });
      }}
      className="bg-card w-full max-w-sm space-y-4 rounded-xl border p-6 shadow-sm"
    >
      <div className="space-y-1.5">
        <Label htmlFor="forgot-password-email">Email</Label>
        <Input
          id="forgot-password-email"
          type="email"
          autoComplete="username"
          placeholder="you@estelle.example"
          required
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
      </div>

      <Button type="submit" className="w-full" disabled={busy}>
        {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
        Send reset link
      </Button>
      <Button type="button" variant="ghost" className="w-full" onClick={onBack}>
        Back to sign in
      </Button>
    </form>
  );
}

/**
 * The sign-in screen (#270 §1, FR-ACL-1, M10-6).
 *
 * The password is checked by the API; this form only collects it and shows
 * what came back. One message covers a wrong password and an unknown
 * address, so the screen cannot be used to discover which addresses exist.
 */
export function SignInScreen() {
  const navigate = useNavigate();
  const { signIn } = useSession();
  const { redirect } = useSignInSearch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [forgotPassword, setForgotPassword] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    setBusy(true);

    void signIn(email, password)
      .then(() => {
        // Back to wherever the route guard intercepted, or the dashboard for
        // a sign-in nobody was redirected into.
        void navigate({ to: redirect ?? "/" });
      })
      .catch((cause: unknown) => {
        setError(
          cause instanceof Error ? cause.message : "Could not sign you in.",
        );
        setBusy(false);
      });
  }

  return (
    <div className="bg-background text-foreground flex min-h-screen flex-col items-center justify-center gap-6 px-4">
      <div className="w-full max-w-sm space-y-1 text-center">
        <h1 className="text-xl font-semibold tracking-tight">EstelleERP</h1>
        <p className="text-muted-foreground text-sm">
          {forgotPassword ? "Reset your password" : "Sign in to your workspace"}
        </p>
      </div>

      {forgotPassword ? (
        <ForgotPasswordForm
          onBack={() => {
            setForgotPassword(false);
          }}
        />
      ) : (
        <form
          onSubmit={handleSubmit}
          className="bg-card w-full max-w-sm space-y-4 rounded-xl border p-6 shadow-sm"
        >
          <div className="space-y-1.5">
            <Label htmlFor="sign-in-email">Email</Label>
            <Input
              id="sign-in-email"
              type="email"
              autoComplete="username"
              placeholder="you@estelle.example"
              required
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="sign-in-password">Password</Label>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground text-xs underline-offset-4 hover:underline"
                onClick={() => {
                  setForgotPassword(true);
                  setError(null);
                }}
              >
                Forgot password?
              </button>
            </div>
            <Input
              id="sign-in-password"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              required
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
          </div>

          {error === null ? null : (
            <p
              role="alert"
              className="text-destructive text-sm"
              data-testid="sign-in-error"
            >
              {error}
            </p>
          )}

          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                Signing in…
              </>
            ) : (
              "Sign in"
            )}
          </Button>
        </form>
      )}
    </div>
  );
}
